<template>
  <div v-if="showSpinner" class="payment-history">
    <LoadingSpinner />
  </div>
  <div v-else class="payment-history">
    <div class="content">
      <PrimaryEmailVerificationWrapperComponent notification-context="PaymentHistoryComponent" :email="email" />
      <header>
        <h1 class="h1">{{ tn("paymentHistory") }}</h1>
      </header>
      <div class="table-to-list-wrapper">
        <table v-if="payments.length > 0">
          <thead>
            <th class="sortable" :class="sortDirection.invoiceDate" @click="sortPayments('invoiceDate')">
              {{ tn("dateCol") }}
            </th>
            <th class="sortable" :class="sortDirection.invoiceId" @click="sortPayments('invoiceId')">
              {{ tn("invoiceCol") }}
            </th>
            <th class="sortable" :class="sortDirection.productNameSort" @click="sortPayments('productNameSort')">
              {{ tn("productCol") }}
            </th>
            <th class="sortable" :class="sortDirection.creditCardType" @click="sortPayments('creditCardType')">
              {{ tn("creditCardCol") }}
            </th>
            <th
              class="sortable"
              :class="sortDirection.invoicePaymentAmount"
              @click="sortPayments('invoicePaymentAmount')"
            >
              {{ tn("total") }}
            </th>
          </thead>
          <tbody>
            <tr v-for="(p, i) in payments" :key="i">
              <td :data-th="tn('dateCol')">
                <span>{{ formatPaymentDate(p) }}</span>
              </td>
              <td :data-th="tn('invoiceCol')">{{ p.invoiceId }}</td>
              <td :data-th="tn('productCol')">
                <ul>
                  <li>{{ t(productDisplayForInvoice(p)) }}</li>
                  <li v-if="isSafePlusOrPrime(p)">{{ t("Brand.webrootSecureAnywhereAntivirus") }}</li>
                  <li v-if="isEvergreenMonthly(p.subscriptionPaymentItems)">
                    {{ tn("quantity") }} {{ getQuantity(p) }} ({{ tn("activeUntilCancelled") }})
                  </li>
                  <li v-else-if="getYears(p.subscriptionPaymentItems) > 0">
                    {{ tn("quantity") }}
                    {{ getQuantity(p) }} ({{ getYears(p.subscriptionPaymentItems) }}{{ tn("year") }})
                  </li>
                  <li v-else>
                    {{ tn("quantity") }} {{ getQuantity(p) }}
                    {{ getDescription(p.subscriptionPaymentItems) }}
                  </li>
                  <li v-if="p.computerName || p.computerDescription">{{ p.computerName || p.computerDescription }}</li>
                </ul>
              </td>
              <td :data-th="tn('creditCardCol')">
                <span v-if="hideCreditCardInfo(p)">{{ tn("notApplicable") }}</span>
                <span v-else>{{ creditCardInfo(p) }}</span>
              </td>
              <td :data-th="tn('total')">{{ formatInvoiceAmount(p) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="payments.length === 0" class="message-box">
        {{ tn("noInvoicesToDisplay") }}
      </div>
      <section v-if="creditBalance">
        <header>
          <h1>{{ tn("accountCredit") }}</h1>
        </header>
        <i18n-t keypath="PaymentHistoryComponent.amountOfCredit" tag="p" scope="global">
          <template #amount>
            <strong>{{ formatMoney(creditBalance) }}</strong>
          </template>
        </i18n-t>
      </section>
      <p>{{ tn("otherTransactionNotice") }}</p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, onMounted } from "vue";
import { logEvent } from "@/common/logger";
import { t } from "@/i18n";
import { unifiedApi } from "@/common";
import {
  IUserPaymentHistory,
  IUserPaymentHistoryPaymentInfo,
  IUserPaymentHistoryPaymentInfoItems,
} from "@/common/api/unifiedPortal/interfaces";
import { useUserStore } from "@/stores/user";
import PrimaryEmailVerificationWrapperComponent from "@/components/shared/PrimaryEmailVerificationWrapper.vue";
import { getProductDisplayNameBySku, IProductDisplayRequest } from "@/common/productsBySku";
import LoadingSpinner from "@/components/shared/LoadingSpinner.vue";
import { handleApiError } from "@/common/handleApiError";
import { AxiosError } from "axios";
import { useNotificationsStore } from "@/stores/notifications";
import { formatDate } from "@/common/dateFormat";

const componentName = "PaymentHistoryComponent";

export default defineComponent({
  name: componentName,
  components: {
    LoadingSpinner,
    PrimaryEmailVerificationWrapperComponent,
  },
  setup() {
    logEvent("created", componentName);

    const userStore = useUserStore();
    const notificationsStore = useNotificationsStore();
    const user = computed(() => userStore.currentUser);
    const creditBalance = ref<number>(0);
    const paymentHistory = ref<IUserPaymentHistory | null>(null);
    const payments = ref<IUserPaymentHistoryPaymentInfo[]>([]);
    const showSpinner = ref(true);
    const sortColumn = ref<string>("");
    const email = ref<string>("");
    const sortDirection = ref<Record<string, unknown>>({
      invoiceDate: "ascend",
      invoiceId: "",
      productNameSort: "",
      creditCardType: "",
      invoicePaymentAmount: "",
    });

    function tn(v: string, params?: Record<string, unknown>): string {
      return t(`${componentName}.${v}`, params);
    }

    onMounted(async () => {
      logEvent("mounted", componentName);
      email.value = user.value?.email ?? "";

      if (user.value?.subscriberInfo?.hasPendingWebrootLicenses && !user.value?.subscriberInfo?.hasValidatedEmail) {
        notificationsStore.addNotification({ type: "PrimaryEmailVerificationWebroot" });
      }
      try {
        if (user.value) {
          const response = (await unifiedApi.getUserPaymentHistory()).data;
          if (response) {
            paymentHistory.value = response;
            creditBalance.value = paymentHistory.value.creditBalance;
            payments.value = processData(paymentHistory.value.payments);
            sortPayments("invoiceDate");
          } else {
            logEvent(`getUserPaymentHistory result NULL`, `${componentName}/onMounted`);
          }
        } else {
          logEvent(`user value NULL`, `${componentName}/onMounted`);
        }
      } catch (err) {
        handleApiError(err as AxiosError, true);
      } finally {
        showSpinner.value = false;
      }
    });

    function processData(invoices: IUserPaymentHistoryPaymentInfo[]) {
      for (const invoice of invoices) {
        invoice.productNameSort = productDisplayForInvoice(invoice);
      }

      return invoices;
    }

    ///At the moment with Safe only invoices we can only have 1 type of product per invoice
    function productDisplayForInvoice(invoiceItem: IUserPaymentHistoryPaymentInfo) {
      logEvent("productDisplayForInvoice", componentName, invoiceItem);
      if (invoiceItem.subscriptionPaymentItems.length === 0) {
        return "";
      }

      const subscriptionItem: IUserPaymentHistoryPaymentInfoItems = invoiceItem.subscriptionPaymentItems[0];
      const request: IProductDisplayRequest = {
        sku: subscriptionItem.sku,
        webrootLicenseKey: subscriptionItem.webrootKeycode,
        isTrial: false,
      };

      return getProductDisplayNameBySku(request);
    }

    function creditCardInfo(invoice: IUserPaymentHistoryPaymentInfo) {
      return `${invoice.creditCardType}*${invoice.creditCardFourDigit}`;
    }

    function hideCreditCardInfo(invoice: IUserPaymentHistoryPaymentInfo) {
      return !invoice.creditCardType || !invoice.creditCardFourDigit;
    }

    function formatInvoiceAmount(invoice: IUserPaymentHistoryPaymentInfo) {
      return new Number(invoice.invoicePaymentAmount).toLocaleString("en-US", { style: "currency", currency: "USD" });
    }

    function formatPaymentDate(invoice: IUserPaymentHistoryPaymentInfo) {
      return formatDate(new Date(invoice.invoiceDate));
    }

    function formatMoney(amount: number) {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
    }

    function getQuantity(invoice: IUserPaymentHistoryPaymentInfo) {
      let quantity = 0;
      //For invoices with upgrades the old item is returned in the subscriptionPaymentItems with 0 for the subscriptionMonths
      //We don't want to count those items towards the QTY

      let items = invoice.productNameSort.toLowerCase().includes("lite")
        ? invoice.subscriptionPaymentItems.filter(l => l.subscriptionMonths) ?? []
        : invoice.subscriptionPaymentItems.filter(l => l.subscriptionMonths > 0 && l.itemAmount > 0) ?? [];

      if (invoice.invoiceId.toLowerCase().startsWith("carb")) {
        items = invoice.subscriptionPaymentItems;
      }

      for (const item of items) {
        quantity += item.quantity;
      }

      return quantity;
    }

    ///An evergreen sub shouldn't ever get to this
    //If there are multiple items they will all have the same amount of time
    //since safe purchases only allow 1 product type per cart and you buy the same
    //years and sku for all of them
    function getYears(subscriptionPaymentItems: IUserPaymentHistoryPaymentInfoItems[]) {
      let years = 1;
      years = subscriptionPaymentItems[0].subscriptionMonths / 12;

      return Math.trunc(years);
    }

    ///Will need to updated once we are returning Ecomm invoices, but will work for Safe
    function isEvergreenMonthly(subscriptionPaymentItems: IUserPaymentHistoryPaymentInfoItems[]) {
      return subscriptionPaymentItems[0].isEvergreenMonthly;
    }

    function isSafePlusOrPrime(invoice: IUserPaymentHistoryPaymentInfo) {
      if (invoice.subscriptionPaymentItems.length > 1) {
        return (
          invoice.subscriptionPaymentItems.some(
            l =>
              l.sku === "PersonalPlus" ||
              l.sku === "PersonalPrime" ||
              l.sku === "CarboniteHomePlus" ||
              l.sku === "CarboniteHomePremier"
          ) &&
          (!invoice.subscriptionPaymentItems.some(l => l.sku === "PersonalBasic") ||
            invoice.subscriptionPaymentItems.some(l => l.sku === "PersonalBasic" && l.itemAmount < 0))
        );
      }

      return invoice.subscriptionPaymentItems.some(
        l =>
          l.sku === "PersonalPlus" ||
          l.sku === "PersonalPrime" ||
          l.sku === "CarboniteHomePlus" ||
          l.sku === "CarboniteHomePremier"
      );
    }

    function sortPayments(field: string) {
      //turn off sorting indicator on old column
      if (sortColumn.value !== field) sortDirection.value[sortColumn.value] = "";

      //pick a direction for the sort
      if (sortDirection.value[field] == "ascend") sortDirection.value[field] = "descend";
      else if (sortDirection.value[field] == "descend") sortDirection.value[field] = "ascend";
      else sortDirection.value[field] = "ascend";

      const order = sortDirection.value[field] === "ascend" ? -1 : 1;

      if (payments.value)
        payments.value = payments.value.sort((a: IUserPaymentHistoryPaymentInfo, b: IUserPaymentHistoryPaymentInfo) => {
          if (a[field] > b[field]) return order;
          if (a[field] < b[field]) return -order;
          return 0;
        });
      sortColumn.value = field;
    }

    function getDescription(subscriptionPaymentItems: IUserPaymentHistoryPaymentInfoItems[]) {
      return subscriptionPaymentItems.length > 0 && subscriptionPaymentItems[0].description
        ? subscriptionPaymentItems[0].description
        : "";
    }
    // expose to template
    return {
      t,
      tn,
      formatPaymentDate,
      productDisplayForInvoice,
      isSafePlusOrPrime,
      isEvergreenMonthly,
      getQuantity,
      getYears,
      creditCardInfo,
      hideCreditCardInfo,
      formatInvoiceAmount,
      creditBalance,
      payments,
      showSpinner,
      sortPayments,
      sortColumn,
      sortDirection,
      formatMoney,
      getDescription,
      email,
    };
  },
});
</script>

<style scoped lang="css">
@import "@/styles/variables.css";

header {
  display: block;
}
p {
  margin-bottom: 40px;
}
tr {
  padding-bottom: var(--space-double);
}
</style>
